<template>
  <zm-dialog
    v-model="visible"
    aria-label="Validate Address for 0ABJ Japan Numbers"
    class="validate-address-0ABJ opc-full-zm-dialog"
    width="544px"
  >
    <template #header>
      <h1 class='validate-address-0ABJ__title'>
        {{ $t('billing.pbx.validate_address') }}
        <span class='validate-address-0ABJ__subtitle'>{{ $t('billing.pbx.required_field') }}</span>
      </h1>
    </template>
    <zm-form
      v-loading='loading'
      style='padding: 4px'
      ref="form"
      :model="form"
      :rules='rules'
    >
      <zm-form-item
        :label="$t('billing.pbx.postal_code')"
        for="postal-code"
        prop='zipCode'
        :error='invalidZipCodeError'
      >
        <zm-input
          v-model="form.zipCode"
          id="postal-code"
          placeholder=''
        />
      </zm-form-item>
      <zm-form-item
        for="prefecture"
        :label="$t('billing.pbx.prefecture')"
        :disabled='isNotEligibleFields("prefecture")'
      >
        <zm-virtual-filter-select
          id="prefecture"
          ref='prefecture'
          placeholder=''
          v-model="form.prefecture"
          :options='options["prefecture"]'
          @change='(value) => handleFieldsChange("prefecture", value)'
        ></zm-virtual-filter-select>
      </zm-form-item>
      <zm-form-item
        for="city-ward"
        :label="$t('billing.pbx.city_town')"
        :disabled='isNotEligibleFields("cityWard")'
      >
        <zm-virtual-filter-select
          id="city-ward"
          ref='cityWard'
          placeholder=''
          v-model="form.cityWard"
          :options='options["cityWard"]'
          @change='(value) => handleFieldsChange("cityWard", value)'
        ></zm-virtual-filter-select>
      </zm-form-item>
      <zm-form-item
        for="local-area"
        :label="$t('billing.pbx.sub_municipality')"
        :disabled='isNotEligibleFields("localArea")'
      >
        <zm-virtual-filter-select
          id="local-area"
          ref='localArea'
          placeholder=''
          v-model="form.localArea"
          :options='options["localArea"]'
          @change='(value) => handleFieldsChange("localArea", value)'
        ></zm-virtual-filter-select>
      </zm-form-item>
      <zm-form-item
        for="land"
        :label="$t('billing.pbx.land')"
        :disabled='isNotEligibleFields("land")'
      >
        <zm-virtual-filter-select
          id="land"
          ref='land'
          placeholder=''
          v-model="form.land"
          :options='options["land"]'
          @change='(value) => handleFieldsChange("land", value)'
        ></zm-virtual-filter-select>
      </zm-form-item>
      <zm-form-item v-if='validateResultCode'>
        <zm-banner
          :content="validateResultMessage"
          :type="validateResultMessageType"
        />
      </zm-form-item>
    </zm-form>
    <template #footer>
      <div>
        <div class="divide-line"></div>
        <div class='validate-address-0ABJ__footer'>
          <zm-button
            v-for='item in ctaList'
            :key='item.type'
            :type='item.ctaType'
            :disabled='item.disabled'
            @click="item.callback"
          >
            {{ item.label }}
          </zm-button>
        </div>
      </div>
    </template>
  </zm-dialog>
</template>

<script type="text/jsx" lang="jsx">
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import { get0ABJAddressInfo, check0ABJInfo } from '@/apis/pbx';

const postalCodeTester = (value) => {
  return /^\d{3}-\d{4}$/.test(value);
};

const eligibleFieldsMap = {
  prefecture: (value) => !!postalCodeTester(value),
  cityWard: (value) => !!value,
  localArea: (value) => !!value,
  land: (value) => !!value,
};

const ValidateCode = {
  success: 200,
  notAvailable: 4001,
  noInventory: 4002,
};

const formatInput = (input) => {
  const numbers = input.replace(/\D/g, '').slice(0, 7);
  const part1 = numbers.slice(0, 3);
  const part2 = numbers.slice(3);
  return part2.length > 0 ? `${part1}-${part2}` : part1;
}

export default {
  name: '0ABJValidateDialog',
  mixins: [ConfirmComponent],
  data() {
    const validatePostalCode = (rule, value, callback) => {
      if (!postalCodeTester(value)) {
        callback(new Error(this.$t('billing.pbx.postal_code_must_be_7_digits')));
      } else if (this.isInvalidZipCode) {
        callback(new Error(this.$t('billing.pbx.invalid_postal_code')));
      } else {
        callback();
      }
    };
    return {
      visible: false,
      form: {
        zipCode: '',
        prefecture: '',
        cityWard: '',
        localArea: '',
        land: ''
      },
      loading: false,
      fields: ['zipCode', 'prefecture', 'cityWard', 'localArea', 'land'],
      requiredFields: ['zipCode', 'prefecture', 'cityWard'],
      currentLevel: 0,
      options: {
        prefecture: [],
        cityWard: [],
        localArea: [],
        land: [],
      },
      rules: {
        zipCode: [
          { required: true, message: this.$t('billing.pbx.postal_code_must_be_7_digits') },
          { validator: validatePostalCode.bind(this), trigger: 'change' },
        ],
      },
      validateResultCode: '',
      validateCodeInfo: {
        200: {
          code: 200,
          messageType: 'success',
          message: this.$t('billing.pbx.your_address_is_eligible'),
        },
        4001: {
          code: 4001,
          messageType: 'warning',
          message: this.$t('billing.pbx.this_address_does_not_qualify_for_0ABJ_number'),
        },
        4002: {
          code: 4002,
          messageType: 'info',
          message: this.$t('billing.pbx.do_not_provide_service_for_0ABJ_number'),
        },
      },
      isInvalidZipCode: false,
    };
  },
  computed: {
    isEligibleValidateCTA() {
      return !this.requiredFields.every(field => !!this.form[field]);
    },
    cta() {
      return {
        continue: { type: 'continue', ctaType: 'primary', label: this.$t('billing.continue'), callback: this.onCancel, disabled: this.isEligibleValidateCTA, },
        validate: { type: 'validate', ctaType: 'primary', label: this.$t('billing.pbx.validate'), callback: this.onValidate, disabled: this.isEligibleValidateCTA, },
        validateCancel: { type: 'validate', ctaType: '', label: this.$t('billing.pbx.validate'), callback: this.onValidateCancel, disabled: this.isEligibleValidateCTA, },
        cancel: { type: 'cancel', ctaType: '', label: this.$t('billing.common.cancel'), callback: this.onCancel, disabled: false, },
        contactSales: { type: 'contactSales', ctaType: 'primary', label: this.$t('billing.common.contact_sales'), callback: this.onContactSales, disabled: this.isEligibleValidateCTA, },
      }
    },
    ctaList() {
      if (this.validateResultCode === ValidateCode.notAvailable) {
        return [this.cta.continue, this.cta.validateCancel]
      } else if (this.validateResultCode === ValidateCode.success) {
        return [this.cta.continue]
      } else if (this.validateResultCode === ValidateCode.noInventory) {
        return [this.cta.contactSales, this.cta.cancel]
      } else {
        return [this.cta.validate, this.cta.cancel]
      }
    },
    validateResultMessageType() {
      return this.validateCodeInfo[this.validateResultCode]?.messageType;
    },
    validateResultMessage() {
      return this.validateCodeInfo[this.validateResultCode]?.message;
    },
    invalidZipCodeError() {
      if (this.isInvalidZipCode) {
        return this.$t('billing.pbx.invalid_postal_code');
      }
      return '';
    },
  },
  methods: {
    async onValidate() {
      try {
        this.loading = true;
        const result = await check0ABJInfo({
          ...this.form,
          zipCode: this.form.zipCode.replace(/-/, '')
        });
        const code = result?.result?.code;
        this.loading = false;
        this.validateResultCode = code;
      } catch (e) {
        this.loading = false;
      }
    },
    onContactSales() {
      window.open('/contactsales', '_blank');
    },
    onCancel() {
      this.isInvalidZipCode = false;
      this.clearValidateStatus();
      this.$refs.form.resetFields();
      this.visible = false;
    },
    onValidateCancel() {
      this.resetForm();
      this.clearValidateStatus();
    },
    resetForm() {
      this.form = {
        zipCode: '',
        prefecture: '',
        cityWard: '',
        localArea: '',
        land: ''
      };
    },
    isNotEligibleFields(field) {
      const index = this.fields.indexOf(field);
      if (index > 0) {
        const preIndex = index - 1;
        const preField = this.fields[preIndex];
        return !eligibleFieldsMap[field](this.form[preField]);
      }
      return true;
    },
    async handleFieldsChange(field, value) {
      if (this.loading) return;
      this.loading = true;
      const index = this.fields.indexOf(field);
      this.form[field] = value;
      this.currentLevel = index + 1;
      this.clearFieldsFrom(index + 1);
      await this.fetchNextLevel(field);
      this.loading = false;
    },
    clearFieldsFrom(index) {
      if (index > 0) {
        this.clearValidateStatus();
        const resetFields = this.fields.slice(index);
        resetFields.forEach(field => {
          this.form[field] = '';
        });
      }
    },
    clearValidateStatus() {
      this.validateResultCode = '';
    },
    async fetchNextLevel(changedField) {
      const currentIndex = this.fields.indexOf(changedField);
      const nextField = this.fields[currentIndex + 1];
      if (!nextField) return;

      const params = this.fields.slice(0, currentIndex + 1)
        .reduce((acc, cur) => ({ ...acc, [cur]: this.form[cur] }), {});

      const data = await this.callNextFieldApi(nextField, params);

      if (!data) return;

      const restFields = this.fields.slice(this.currentLevel);

      this.isInvalidZipCode = this.checkIfPostalCodeIsValid(this.requiredFields.slice(this.currentLevel), data);

      for (let i = 0; i < restFields.length; i++) {
        const field = restFields[i];
        const value = data[field];
        this.currentLevel = this.fields.indexOf(field);
        this.clearFieldsFrom(this.currentLevel);
        this.options[field] = value.map(item => ({value: item, label: item}));
        // auto fill
        if (value.length === 1) {
          this.form[field] = value[0];
        } else if (value.length > 1) {
          // need to be selected manually by the customer
          this.clearFieldsFrom(this.currentLevel);
          this.$nextTick(() => {
            this.focusNextLevelField();
          });
          break;
        } else {
          this.clearFieldsFrom(this.currentLevel);
          break;
        }
      }
    },
    async callNextFieldApi(field, params) {
      const result = await get0ABJAddressInfo(this.getParams(field, params));
      const address = result?.result;
      return {
        prefecture: address?.prefectures ?? [],
        cityWard: address?.cityWards ?? [],
        localArea: address?.localAreas ?? [],
        land: address?.lands ?? [],
      }
    },
    getParams(type = '', params = {}) {
      const p = {
        type: type.toUpperCase(),
        ...params,
        zipCode: params.zipCode.replace(/-/, ''),
      };
      return p;
    },
    focusNextLevelField() {
      const field = this.fields[this.currentLevel];
      if (field) {
        const ref = this.$refs?.[field];
        if (ref) {
          ref.visible = true;
        }
      }
    },
    checkIfPostalCodeIsValid(restFields, data) {
      // invalid zip code
      let isInvalidZipCode = false;
      for (let i = 0; i < restFields.length; i++) {
        const field = restFields[i];
        if (data?.[field]) {
          if (data[field]?.length >= 1) {
            isInvalidZipCode = false;
          } else {
            isInvalidZipCode = true;
            break;
          }
        } else {
          continue;
        }
      }
      return isInvalidZipCode;
    },
  },
  watch: {
    visible: {
      handler(val) {
        !val && this.resetForm();
      },
    },
    'form.zipCode': {
      handler(value, oldValue) {
        let newValue = value.replace(/[^0-9]/g, "");

        // remove action
        if (oldValue.length > newValue.length) {
          // copy action
          if (oldValue.length === 8 && newValue === value) {
            newValue = newValue.slice(0, 3) + "-" + newValue.slice(3, 7);
          } else {
            newValue = value.slice(0, 8);
          }
        } else if (newValue.length >= 3) {
          newValue = newValue.slice(0, 3) + "-" + newValue.slice(3, 7);
        }

        this.form.zipCode = formatInput(newValue);
        this.$refs.form.validateField('zipCode');

        if (newValue === oldValue) return;

        this.clearFieldsFrom(1);

        if (postalCodeTester(newValue) && !this.loading) {
          this.loading = true;
          this.currentLevel = 1;
          this.clearFieldsFrom(1);
          this.fetchNextLevel('zipCode').then(() => {
            this.loading = false;
          });
        }
      },
    },
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
.validate-address-0ABJ {
  .zbo-virtual-filter-select {
    width: 100%;
  }

  &__title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
  }

  &__subtitle {
    color: var(--zoom-color-text-strong-neutral, #686F79);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    display: block;
    margin-top: 4px;
    &::before {
      content: '*';
      color: var(--zoom-color-text-error, #DD004A);
      margin-right: 2px;
    }
  }

  // .zbo-form-item {
    // margin-bottom: 16px;
    // &__widgets {
      // position: relative;
      // .zbo-input__inner.is-errored {
      //   color: #B22424;
      // }
    // }
    // &__error-icon {
    //   display: none;
    // }
    // &[for='postal-code'] {
    //   .zbo-form-item__label {
    //     &::after {
    //       content: '*';
    //       color: var(--zoom-color-text-neutral, #686F79);
    //     }
    //   }
    // }
  // }

  .divide-line {
    margin-top: 0;
  }
}

</style>
